import { fieldDef, OPS_BY_KIND, type FieldKind } from "./fields.ts";
import { isEmptyFilter } from "./types.ts";

export const MAX_DEPTH = 2; // top-level group + one nested group (spec 05 §7)
// Caps shared with private.segment_node_sql (T21). Ids fit a Postgres integer (2^31 - 1).
export const MAX_CONDITIONS = 200;
export const MAX_LIST_ITEMS = 1000;
export const MAX_DAYS = 36500;
export const MAX_ID = 2_147_483_647;
// A date-only value is UTC midnight; a value with a time must carry Z or an explicit offset.
const ISO =
  /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2}))?$/;
const isIso = (v: unknown) =>
  typeof v === "string" && ISO.test(v) && !Number.isNaN(Date.parse(v));
const strList = (v: unknown) =>
  Array.isArray(v) &&
  v.length > 0 &&
  v.length <= MAX_LIST_ITEMS &&
  v.every((x) => typeof x === "string");
const posInt = (v: unknown) =>
  Number.isInteger(v) && (v as number) > 0 && (v as number) <= MAX_ID;

export function valueOk(kind: FieldKind, op: string, v: unknown): boolean {
  if (op === "exists" || op === "not_exists") return true;
  switch (kind) {
    case "text":
      return op === "in" || op === "nin" ? strList(v) : typeof v === "string";
    case "number":
      return typeof v === "number" && Number.isFinite(v);
    case "bool":
      return typeof v === "boolean";
    case "date":
      return op === "days_since_gte"
        ? Number.isInteger(v) && (v as number) >= 0 && (v as number) <= MAX_DAYS
        : isIso(v);
    case "deal_stage":
      return op === "in" ? strList(v) : typeof v === "string";
    case "tag":
    case "campaign_open":
    case "campaign_click":
      return posInt(v);
  }
}

export type SegmentValidation = { ok: true } | { ok: false; errors: string[] };

export function validateSegmentFilter(filter: unknown): SegmentValidation {
  if (isEmptyFilter(filter)) return { ok: true };
  if (
    typeof filter !== "object" ||
    filter === null ||
    Array.isArray(filter) ||
    !("all" in filter || "any" in filter)
  )
    return { ok: false, errors: ["root_not_group"] };
  const errors: string[] = [];
  let conditions = 0;
  const visit = (node: unknown, depth: number) => {
    if (typeof node !== "object" || node === null || Array.isArray(node)) {
      errors.push("invalid_node");
      return;
    }
    const n = node as Record<string, unknown>;
    if ("all" in n || "any" in n) {
      if (depth > MAX_DEPTH) {
        errors.push("too_deep");
        return;
      }
      const key = "all" in n ? "all" : "any";
      if (Object.keys(n).length !== 1 || !Array.isArray(n[key])) {
        errors.push("invalid_group");
        return;
      }
      for (const child of n[key] as unknown[]) visit(child, depth + 1);
      return;
    }
    conditions++;
    const field = typeof n.field === "string" ? n.field : "";
    const op = typeof n.op === "string" ? n.op : "";
    const def = fieldDef(field);
    if (!def) return void errors.push(`unknown_field:${field}`);
    if (!OPS_BY_KIND[def.kind].includes(op))
      return void errors.push(`bad_operator:${field}:${op}`);
    if (!valueOk(def.kind, op, n.value)) errors.push(`bad_value:${field}`);
  };
  visit(filter, 1);
  if (conditions > MAX_CONDITIONS)
    return { ok: false, errors: ["too_many_conditions"] };
  return errors.length ? { ok: false, errors } : { ok: true };
}
